import { Box, Flex, Heading, Spinner, Text, VStack } from '@chakra-ui/react';
import { PlatformRequirementDocumentVerdict } from '@client/graphql/__generated__/types';
import { FC, useState } from 'react';
import { match } from 'ts-pattern';
import {
  ChecklistIssueView,
  ChecklistIssuesList,
} from '~/common/pages/Compliance/components/ChecklistIssuesList';
import { ValidationWarningBanner } from '~/common/pages/Compliance/components/ValidationWarningBanner';
import { countChecklistIssueGroups } from '~/common/pages/Compliance/components/checklistIssueGrouping';
import { DocumentSuggestionsList } from './DocumentSuggestionsList';
import { IgnoreSuggestionModal } from './IgnoreSuggestionModal';
import { SuccessfulChecksList } from './SuccessfulChecksList';
import {
  CompliancePlatformRequirementIssue,
  ComplianceSuccessfulCheck,
  ComplianceSuggestion,
} from './types';

/**
 * Maps the agent-review checklist mismatch payload into the shared
 * ChecklistIssuesList view-model using only persisted evaluation evidence.
 */
const toChecklistIssueView = (
  issue: CompliancePlatformRequirementIssue
): ChecklistIssueView => {
  return {
    publicIssueId: issue.publicIssueId,
    status: issue.status,
    problemType: issue.problemType,
    groupKey: issue.groupKey,
    evaluationRunId: issue.evaluationRunId,
    evaluatedAt: issue.evaluatedAt,
    isAgentActionable: issue.isAgentActionable,
    checklistItemName: issue.checklistItemName,
    checklistSectionName: issue.checklistSectionName,
    operator: issue.operator,
    allowedDocumentCategories: issue.allowedDocumentCategories,
    // Prefer friendly-name references; legacy rows fall back to raw codes.
    allowedForms:
      (issue.allowedFormReferences ?? []).length > 0
        ? (issue.allowedFormReferences ?? []).map((reference) => ({
            code: reference.code,
            name: reference.name ?? null,
          }))
        : issue.allowedForms.map((code) => ({ code, name: null })),
    ignoreReason: issue.ignoredReason,
    evaluationDocuments: issue.recordedEvaluationDocuments.map((document) => ({
      documentName: document.documentName,
      isProblemDocument: match(document.verdict)
        .with(PlatformRequirementDocumentVerdict.MISMATCHED, () => true)
        .with(
          PlatformRequirementDocumentVerdict.MATCHED,
          PlatformRequirementDocumentVerdict.UNCONFIGURED,
          PlatformRequirementDocumentVerdict.UNRESOLVED,
          () => false
        )
        .exhaustive(),
      // Recorded evidence, badged directly by the hover tooltip — see the
      // matching note in `toChecklistIssueView`.
      verdict: document.verdict,
      ingestionDocumentId: document.ingestionDocumentId,
      platformDocumentId: document.platformDocumentId,
      contractDocuments: (document.contractDocuments ?? []).map(
        (contractDocument) => ({
          form: contractDocument.form ?? null,
          formName: contractDocument.formName ?? null,
          documentCategory: contractDocument.documentCategory ?? null,
        })
      ),
    })),
  };
};

interface DocumentIssuesPanelProps {
  documentVersionRejectionReasons?: string[] | null;
  suggestions: ComplianceSuggestion[];
  platformRequirementIssues: CompliancePlatformRequirementIssue[];
  successfulChecks: ComplianceSuccessfulCheck[];
  /**
   * Whether the folder uses checklist requirements. Non-checklist platform
   * copy drops the "checklist" wording (e.g. from the empty state).
   */
  isChecklistPlatform: boolean;
  /**
   * How many contract documents the selected PDF holds. Passed to both lists so
   * per-document headings appear whenever the PDF is multi-document, even if
   * only one document produced rows.
   */
  contractDocumentCount?: number;
  /** True once the active tab's detail has loaded. */
  hasDetail: boolean;
  loading: boolean;
  /** Called with a row's field ids when a suggestion/check row is clicked. */
  onSelectFields: (fieldIds: string[]) => void;
  /**
   * Called to hide a suggestion (document-level ignore), with the free-text
   * reason the agent typed in the ignore prompt, or `null` when they left it
   * blank — which clears any reason stored by an earlier ignore.
   */
  onIgnoreSuggestion: (
    suggestion: ComplianceSuggestion,
    message: string | null
  ) => void | Promise<void>;
  /** Called with an already-ignored suggestion to un-hide it (Undo). */
  onUndoIgnoreSuggestion: (
    suggestion: ComplianceSuggestion
  ) => void | Promise<void>;
  /**
   * Called to hide a checklist group's open mismatches, with the free-text
   * reason the agent typed in the ignore prompt, or `null` when they left it
   * blank — which clears any reason stored by an earlier ignore. The group is
   * passed whole because one reason covers all of its issues.
   */
  onIgnorePlatformRequirementIssue: (
    issues: CompliancePlatformRequirementIssue[],
    message: string | null
  ) => void | Promise<void>;
  /** Called with a checklist group's already-ignored mismatches to un-hide them. */
  onUndoIgnorePlatformRequirementIssue: (
    issues: CompliancePlatformRequirementIssue[]
  ) => void | Promise<void>;
  /** True while a document-level ignore mutation is in flight. */
  isIgnoring: boolean;
  /** True while a checklist mismatch ignore mutation is in flight. */
  isIgnoringPlatformRequirementIssue: boolean;
}

/**
 * The selected tab's suggestions, checklist mismatches, and passing checks
 * (derived client-side from the raw detail). Lives in the center column beside
 * the PDF in the redesigned layout — the checklist-level action buttons moved to
 * the title bar, so this panel is read/ignore only.
 *
 * Clicking a suggestion/check row calls `onSelectFields(row.fieldIds)`, which
 * the controller lifts into `activeFieldId` to scroll/center the PDF.
 */
export const DocumentIssuesPanel: FC<DocumentIssuesPanelProps> = ({
  documentVersionRejectionReasons = [],
  suggestions,
  platformRequirementIssues,
  successfulChecks,
  isChecklistPlatform,
  contractDocumentCount,
  hasDetail,
  loading,
  onSelectFields,
  onIgnoreSuggestion,
  onUndoIgnoreSuggestion,
  onIgnorePlatformRequirementIssue,
  onUndoIgnorePlatformRequirementIssue,
  isIgnoring,
  isIgnoringPlatformRequirementIssue,
}) => {
  // Ignoring a checklist group prompts for an optional reason first, so the
  // group's open issues are held here until the modal resolves. Declared above
  // the early returns below — a hook after them changes hook order the moment a
  // document goes from "no findings" to some.
  const [pendingIgnoreIssues, setPendingIgnoreIssues] = useState<
    CompliancePlatformRequirementIssue[] | null
  >(null);
  const [pendingChecklistAction, setPendingChecklistAction] = useState<{
    action: 'ignore' | 'undo';
    publicIssueIds: string[];
  } | null>(null);

  const runChecklistAction = async (
    action: 'ignore' | 'undo',
    issues: CompliancePlatformRequirementIssue[],
    operation: () => void | Promise<void>
  ): Promise<void> => {
    setPendingChecklistAction({
      action,
      publicIssueIds: issues.map((issue) => issue.publicIssueId),
    });
    try {
      await operation();
    } finally {
      setPendingChecklistAction(null);
    }
  };

  // Resolves the shared list's view-models back to the payload issues the page's
  // mutation is keyed off.
  const resolveIssues = (
    views: ChecklistIssueView[]
  ): CompliancePlatformRequirementIssue[] =>
    views.flatMap(
      (view) =>
        platformRequirementIssues.find(
          (candidate) => candidate.publicIssueId === view.publicIssueId
        ) ?? []
    );

  if (loading && !hasDetail) {
    return (
      <Flex justify="center" py={8}>
        <Spinner color="compliance.loading.text" />
      </Flex>
    );
  }

  const hasFindings =
    (documentVersionRejectionReasons?.length ?? 0) > 0 ||
    suggestions.length > 0 ||
    platformRequirementIssues.length > 0 ||
    successfulChecks.length > 0;

  const checklistIssueViews =
    platformRequirementIssues.map(toChecklistIssueView);
  // Counted off the rendered groups, not the raw issue list: several issues on
  // one checklist requirement collapse into a single row, and a heading that
  // disagrees with the rows below it is worse than no heading.
  const checklistGroupCount = countChecklistIssueGroups(checklistIssueViews);
  const checklistHeading =
    checklistGroupCount === 1
      ? '1 checklist suggestion'
      : `${checklistGroupCount} checklist suggestions`;

  if (!hasFindings) {
    return (
      <Text color="compliance.text.muted" fontSize="sm">
        {isChecklistPlatform
          ? 'No validation suggestions, checklist suggestions, or checks for this document.'
          : 'No validation suggestions or checks for this document.'}
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={5}>
      {documentVersionRejectionReasons?.map((message) => (
        <ValidationWarningBanner key={message} message={message} />
      ))}
      <DocumentSuggestionsList
        contractDocumentCount={contractDocumentCount}
        isIgnoring={isIgnoring}
        suggestions={suggestions}
        onIgnore={onIgnoreSuggestion}
        onSelectFields={onSelectFields}
        onUndoIgnore={onUndoIgnoreSuggestion}
      />
      {platformRequirementIssues.length > 0 ? (
        // Same card chrome and heading voice as DocumentSuggestionsList: to the
        // agent these are one more kind of suggestion, not a separate system.
        <Box
          bg="whiteAlpha.50"
          borderColor="whiteAlpha.100"
          borderRadius="10px"
          borderWidth={1}
          p={5}
        >
          <VStack align="stretch" spacing={2}>
            <Heading
              color="compliance.status.warning"
              fontSize="lg"
              fontWeight="normal"
              size="headline"
            >
              {checklistHeading}
            </Heading>
            <ChecklistIssuesList
              showStatusChip
              actions={{
                isIgnoring: isIgnoringPlatformRequirementIssue,
                pendingAction: pendingChecklistAction,
                onIgnore: (views) => {
                  const issues = resolveIssues(views);
                  if (issues.length > 0) {
                    setPendingIgnoreIssues(issues);
                  }
                },
                onUndoIgnore: (views) => {
                  const issues = resolveIssues(views);
                  if (issues.length > 0) {
                    // Undo writes in the background: the rows un-cross from the
                    // optimistic cache patch and the handler reports its own
                    // failures, so there is nothing here to await or surface.
                    void runChecklistAction('undo', issues, () =>
                      onUndoIgnorePlatformRequirementIssue(issues)
                    );
                  }
                },
              }}
              evaluationStatus="evaluated"
              issues={checklistIssueViews}
              rowVariant="suggestion"
            />
          </VStack>
        </Box>
      ) : null}
      <SuccessfulChecksList
        checks={successfulChecks}
        contractDocumentCount={contractDocumentCount}
        onSelectFields={onSelectFields}
      />

      <IgnoreSuggestionModal
        isLoading={isIgnoringPlatformRequirementIssue}
        isOpen={pendingIgnoreIssues != null}
        placeholder="Why is this checklist issue not relevant? (optional)"
        title="Ignore checklist issue"
        onClose={() => setPendingIgnoreIssues(null)}
        // Returned so the modal can await the write: it keeps the spinner up and
        // stays open if the ignore fails, rather than closing on a result it
        // never saw.
        onConfirm={(message) => {
          if (pendingIgnoreIssues == null) {
            return;
          }
          const issues = pendingIgnoreIssues;

          return runChecklistAction('ignore', issues, () =>
            onIgnorePlatformRequirementIssue(
              issues,
              message === '' ? null : message
            )
          );
        }}
      />
    </VStack>
  );
};
